import { useQuery } from "@tanstack/react-query";
import {
  Box, Card, CardContent, Chip, Stack, Typography, Alert, Divider, Skeleton,
} from "@mui/material";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

interface Section {
  items: any[];
  stale: boolean;
  error?: string;
  note?: string;
}
interface SettingsResponse {
  tenant: string;
  datasets: Section;
  templates: Section;
  integrations: Section;
  cronjobs: Section;
}

function SectionCard({
  title, Icon, iconColor, section, renderItem,
}: {
  title: string;
  Icon: any;
  iconColor: string;
  section?: Section;
  renderItem?: (i: any, idx: number) => React.ReactNode;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Icon sx={{ color: iconColor }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "primary.dark" }}>{title}</Typography>
        </Stack>
        {!section && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} height={30} />)}
        {section?.stale && (
          <Alert severity="info" icon={<ConstructionRoundedIcon fontSize="small" />} sx={{ mb: 1 }}>
            {section.note ?? `Indisponível (${section.error ?? "unknown"}).`}
          </Alert>
        )}
        {section && section.items.length > 0 && (
          <Stack spacing={1} divider={<Divider />}>
            {section.items.map((item, idx) => renderItem ? renderItem(item, idx) : (
              <Typography key={idx} variant="body2">{JSON.stringify(item)}</Typography>
            ))}
          </Stack>
        )}
        {section && !section.stale && section.items.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nada configurado.</Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function Settings() {
  const { tenant } = useTenant();
  const api = useApi();
  const q = useQuery({
    queryKey: ["settings", tenant],
    queryFn: () => api.call<SettingsResponse>("/api/settings"),
    enabled: !!tenant, staleTime: 60_000,
  });
  const s = q.data;

  return (
    <>
      <PageHeader
        eyebrow="Gestão"
        title="Ambiente"
        subtitle={`Retrato de leitura do ambiente de ${tenant ?? "?"} · nada aqui é editável.`}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <SectionCard
          title="Base do cliente" Icon={StorageRoundedIcon} iconColor="primary.main"
          section={s?.datasets}
          renderItem={(d, i) => (
            <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{d.name}</Typography>
                {d.table_count !== undefined && (
                  <Typography variant="caption" color="text.secondary">{d.table_count} tabelas</Typography>
                )}
              </Box>
              <Chip label={d.status === "ok" ? "OK" : d.status === "empty" ? "vazio" : "?"}
                size="small" color={d.status === "ok" ? "success" : "default"} variant="outlined" />
            </Stack>
          )}
        />

        <SectionCard
          title="Templates WhatsApp" Icon={ChatBubbleRoundedIcon} iconColor="success.main"
          section={s?.templates}
          renderItem={(t, i) => (
            <Stack key={i} direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{t.name}</Typography>
              <Chip label={t.status} size="small" variant="outlined" />
            </Stack>
          )}
        />

        <SectionCard
          title="Integrações" Icon={ExtensionRoundedIcon} iconColor="info.main"
          section={s?.integrations}
          renderItem={(x, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{x.name}</Typography>
              <Chip label={x.status} size="small"
                color={x.status === "active" ? "success" : "default"} variant="outlined" />
            </Stack>
          )}
        />

        <SectionCard
          title="Cronjobs" Icon={ScheduleRoundedIcon} iconColor="secondary.dark"
          section={s?.cronjobs}
          renderItem={(c, i) => (
            <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.name}</Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>{c.cron}</Typography>
              </Box>
              <Chip label={c.state} size="small" color="success" variant="outlined" />
            </Stack>
          )}
        />
      </Box>

      {/* dizia que "mudancas aqui geram commit no repo": a tela nao altera nada */}
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Tela de leitura. Mudanças de configuração são feitas no repositório owlia (GitOps).
      </Alert>
    </>
  );
}
